import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface FilterResult {
  isBlocked: boolean;
  isFlagged: boolean;
  category?: 'political' | 'sensitive' | 'predatory' | null;
  reason?: string;
}

interface FilterCategory {
  name: string;
  keywords: string[];
  blockThreshold: number;
  contextPatterns: RegExp[];
}

// Define filter categories and their associated keywords/patterns
const filterCategories: FilterCategory[] = [
  {
    name: 'political',
    keywords: [
      'election', 'vote', 'democrat', 'republican', 'liberal', 'conservative',
      'protest', 'rally', 'activism', 'socialist', 'communist', 'fascist',
      'propaganda', 'campaign', 'politician', 'congress', 'senate',
      'revolution', 'protest', 'activist', 'movement', 'rights'
    ],
    blockThreshold: 2,
    contextPatterns: [
      /\b(support|oppose|fight(\s+for)?|advocate)\s+(movement|rights|justice|equality)\b/i,
      /\b(left|right)(-|\s+)wing\b/i,
      /\b(social|political)\s+(change|reform|justice|agenda)\b/i
    ]
  },
  {
    name: 'sensitive',
    keywords: [
      'gender', 'sexuality', 'orientation', 'identity', 'transition',
      'pride', 'queer', 'gay', 'lesbian', 'bisexual', 'transgender',
      'pronoun', 'binary', 'attraction', 'relationship'
    ],
    blockThreshold: 2,
    contextPatterns: [
      /\b(sexual|gender)\s+(identity|expression|orientation)\b/i,
      /\b(trans|cis)\s*gender\b/i,
      /\bprefer(\s+my)?\s+pronouns\b/i
    ]
  },
  {
    name: 'predatory',
    keywords: [
      'sex', 'porn', 'nude', 'naked', 'explicit', 'adult', 'xxx',
      'dating', 'meet', 'hookup', 'abuse', 'assault', 'rape',
      'kill', 'suicide', 'self-harm', 'drug', 'weapon', 'bomb'
    ],
    blockThreshold: 1, // Stricter threshold for predatory content
    contextPatterns: [
      /\b(how\s+to\s+make|build|create)\s+(bomb|weapon|explosive)\b/i,
      /\b(buy|sell|deal)\s+(drug|substance|pill)\b/i,
      /\b(meet|find)\s+(girl|boy|kid|teen|child)\b/i,
      /\b(share|send|show)\s+(pic|photo|image|video)\b/i
    ]
  }
];

// Utility function to count keyword matches
function countKeywordMatches(text: string, keywords: string[]): number {
  const words = text.toLowerCase().split(/\s+/);
  return keywords.reduce((count, keyword) => {
    return count + (words.includes(keyword.toLowerCase()) ? 1 : 0);
  }, 0);
}

// Utility function to check context patterns
function matchesContextPatterns(text: string, patterns: RegExp[]): boolean {
  return patterns.some(pattern => pattern.test(text));
}

// Main filtering function
export async function filterContent(text: string): Promise<FilterResult> {
  // Initialize result
  const result: FilterResult = {
    isBlocked: false,
    isFlagged: false,
    category: null,
    reason: ''
  };

  // Check each category
  for (const category of filterCategories) {
    const keywordMatches = countKeywordMatches(text, category.keywords);
    const hasContextMatch = matchesContextPatterns(text, category.contextPatterns);

    if (keywordMatches >= category.blockThreshold || hasContextMatch) {
      result.isBlocked = category.name === 'predatory';
      result.isFlagged = !result.isBlocked;
      result.category = category.name as FilterResult['category'];
      result.reason = `Content contains ${category.name} themes`;
      
      // Log flagged content for review if not blocked
      if (result.isFlagged) {
        await logFlaggedContent(text, category.name);
      }
      
      break;
    }
  }

  return result;
}

// Function to log flagged content for review
async function logFlaggedContent(content: string, category: string) {
  try {
    await supabase
      .from('flagged_content')
      .insert([
        {
          content,
          category,
          status: 'pending_review',
          created_at: new Date().toISOString()
        }
      ]);
  } catch (error) {
    console.error('Error logging flagged content:', error);
  }
}

// Function to report inappropriate content
export async function reportContent(
  contentId: string,
  userId: string,
  reason: string
): Promise<void> {
  try {
    await supabase
      .from('content_reports')
      .insert([
        {
          content_id: contentId,
          reported_by: userId,
          reason,
          status: 'pending_review',
          created_at: new Date().toISOString()
        }
      ]);
  } catch (error) {
    console.error('Error reporting content:', error);
  }
}

// Function to check if user has been flagged for suspicious activity
export async function checkUserTrust(userId: string): Promise<boolean> {
  try {
    const { data: reports } = await supabase
      .from('content_reports')
      .select('id')
      .eq('reported_by', userId)
      .eq('status', 'confirmed_inappropriate')
      .limit(3);

    return !(reports && reports.length >= 3);
  } catch (error) {
    console.error('Error checking user trust:', error);
    return true; // Default to trusting user if check fails
  }
}